import { sanitizeHtml } from '../../utils/safeHtml';
import React, { useState, useEffect } from 'react';
import radiologyResultService from '../../services/radiologyResultService';
import './RadiologyPreviousReportsModal.css';

/**
 * RadiologyPreviousReportsModal
 * Displays previous imaging reports for the current patient.
 * Allows radiologist to view past findings, comparative impressions,
 * and securely create a NEW transaction copy into the current report.
 * (The historical report remains 100% immutable).
 */
export default function RadiologyPreviousReportsModal({
  isOpen,
  onClose,
  testId,
  patientName,
  uhid,
  onCopyReport
}) {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (!isOpen || !testId) return;

    const fetchHistory = async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const res = await radiologyResultService.getPreviousReports(testId);
        if (res.success && res.data) {
          const list = res.data || [];
          setReports(list);
          if (list.length > 0) {
            setSelectedReport(list[0]);
          } else {
            setSelectedReport(null);
          }
        }
      } catch (err) {
        console.error('Error loading previous radiology reports:', err);
        setErrorMsg('Failed to load patient imaging history.');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [isOpen, testId]);

  if (!isOpen) return null;

  const handleCopy = () => {
    if (!selectedReport) return;
    onCopyReport(selectedReport);
    onClose();
  };

  return (
    <div className="rad-modal-overlay">
      <div className="rad-prev-modal">
        {/* Header */}
        <div className="rad-prev-header">
          <div>
            <h2>
              <span>📜</span> Patient Prior Imaging Reports History
            </h2>
            <p>
              Patient: <strong>{patientName}</strong> &bull; UHID: <strong>{uhid}</strong>
            </p>
          </div>
          <button className="rad-modal-close" onClick={onClose}>✕</button>
        </div>

        {errorMsg && (
          <div className="rad-prev-error">
            {errorMsg}
          </div>
        )}

        <div className="rad-prev-body">
          {/* Left Column: Historical Reports List */}
          <div className="rad-prev-list-col">
            <div className="rad-prev-list-header">
              <span>Prior Imaging Studies ({reports.length})</span>
              {loading && <span className="rad-prev-spinner">Loading...</span>}
            </div>

            <div className="rad-prev-list">
              {reports.length === 0 && !loading ? (
                <div className="rad-prev-empty">
                  <p>No prior radiology reports found for this patient.</p>
                </div>
              ) : (
                reports.map(r => {
                  const isSelected = selectedReport?.testId === r.testId;
                  return (
                    <div
                      key={r.testId}
                      className={`rad-prev-item ${isSelected ? 'active' : ''}`}
                      onClick={() => setSelectedReport(r)}
                    >
                      <div className="rad-prev-item-top">
                        <span className="rad-prev-item-title">{r.itemName}</span>
                        <span className="rad-prev-item-date">{new Date(r.date).toLocaleDateString()}</span>
                      </div>
                      <div className="rad-prev-item-sub">
                        <span>{r.department || 'Radiology'} &bull; #{r.labNo}</span>
                        <span className="rad-prev-badge">{r.status}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Column: Historical Report Preview */}
          <div className="rad-prev-view-col">
            <div className="rad-prev-view-header">
              <span>Historical Study Content (Immutable)</span>
              {selectedReport && (
                <span className="rad-prev-view-tag">
                  Signed by {selectedReport.approvedBy || 'Consultant Radiologist'}
                </span>
              )}
            </div>

            <div className="rad-prev-view-content">
              {selectedReport ? (
                <div className="rad-prev-view-doc">
                  <div className="rad-prev-meta-box">
                    <div><strong>Study:</strong> {selectedReport.itemName}</div>
                    <div><strong>Date:</strong> {new Date(selectedReport.date).toLocaleString()}</div>
                    {selectedReport.examinationTechnique && (
                      <div><strong>Technique:</strong> {selectedReport.examinationTechnique}</div>
                    )}
                  </div>

                  <h4>Findings:</h4>
                  <div
                    className="rad-prev-html-box"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedReport.findings || '<p><em>No findings recorded</em></p>') }}
                  />

                  <h4>Impression:</h4>
                  <div
                    className="rad-prev-html-box impression"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedReport.impression || '<p><em>No impression recorded</em></p>') }}
                  />

                  {selectedReport.recommendation && (
                    <>
                      <h4>Recommendation:</h4>
                      <div className="rad-prev-html-box">
                        {selectedReport.recommendation}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="rad-prev-no-selection">
                  <p>Select a prior study from the list on the left to review its findings and impression.</p>
                </div>
              )}
            </div>

            <div className="rad-prev-safety-callout">
              ⚠️ <strong>Safe Transaction Copy:</strong> Clicking "Copy Previous Report" clones these findings into your active report transaction. The original historical report remains 100% unchanged. Review carefully before finalizing.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="rad-prev-footer">
          <button type="button" className="rad-btn-cancel" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="rad-btn-copy-prev"
            disabled={!selectedReport}
            onClick={handleCopy}
            title="Create a new transaction copy based on this prior report"
          >
            📋 Copy Previous Report to Current
          </button>
        </div>
      </div>
    </div>
  );
}
